import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TopDevicesRequestOptions } from '../model/top-devices-analytics';
import { environment } from 'src/environments/environment';
import { RrtMidlaySelectionResponse } from '../model/vce-vhub';

@Injectable({
  providedIn: 'root'
})
export class VceVhubService {
  constructor(private http: HttpClient) { }

  private macPath(value: string) {
    return String(value || '').trim();
  }

  generateWGKey(): Observable<any> {
    return this.http.get<any>(environment.endpoints.vceVhub + '/gen-wireguard-key')
  }

  createVceVhub(data: any): Observable<any> {
    return this.http.post<any>(environment.endpoints.vceVhub, data)
  }

  loadVceConnection(mac: string): Observable<any> {
    return this.http.get<any>(environment.endpoints.vceVhub + '?vhub_mac=' + mac)
  }

  loadVHubConnection(mac: string): Observable<any> {
    return this.http.get<any>(environment.endpoints.vceVhub + '?vce_mac=' + mac)
  }

  deleteConnection(_id: any) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: {
        _id,
        type: 'delete'
      }
    };
    return this.http.delete<any>(environment.endpoints.vceVhub, options)
  }

  toggleConnection(_id: any, b: boolean) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: {
        _id: _id,
        type: 'disable',
        value: b
      }
    };
    return this.http.delete<any>(environment.endpoints.vceVhub, options)
  }

  createRelation(data: any): Observable<any> {
    return this.http.post<any>(environment.endpoints.vceVhub + '/vhub-org-relation', data)
  }

  getRelatedDevices(orgId: string): Observable<any> {
    return this.http.get<any>(environment.endpoints.vceVhub + '/vhub-org-relation?orgId=' + orgId)
  }

  getRelatedOrganizations(mac: string): Observable<any> {
    return this.http.get<any>(environment.endpoints.vceVhub + '/vhub-org-relation?mac=' + mac)
  }

  getRas(mac: string): Observable<any> {
    return this.http.get<any>(environment.endpoints.ras + '?mac=' + mac)
  }

  getVceConfig(data: any): Observable<any> {
    return this.http.post<any>(environment.endpoints.config, data)
  }

  getVhubConfig(data: any): Observable<any> {
    return this.http.post<any>(environment.endpoints.config, data)
  }

  getMidlayLaneAnalytics(deviceId: string, range: string, type: string, mac?: string): Observable<any> {
    let params = new HttpParams()
      .set('range', range)
      .set('type', type)
      .set('skip', '0');
    if (mac) {
      params = params.set('mac', this.macPath(mac));
    }
    return this.http.get<any>(
      environment.endpoints.monitor + '/rrt_midlay/' + encodeURIComponent(deviceId) + '/statistics/values',
      { params }
    )
  }

  getMidlayAnalyticsDevices(deviceId: string): Observable<string[]> {
    return this.http.get<string[]>(
      environment.endpoints.monitor + '/rrt_midlay/' + encodeURIComponent(deviceId) + '/statistics/devices'
    );
  }

  getMidlayTopDevices(deviceId: string, options: TopDevicesRequestOptions): Observable<any> {
    let params = new HttpParams();
    Object.keys(options || {}).forEach(key => {
      const value = options[key];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<any>(
      environment.endpoints.monitor + '/rrt_midlay/' + encodeURIComponent(deviceId) + '/statistics/top-devices',
      { params }
    );
  }

  getMidlaySelectionOptions(vceMac?: string, vhubMac?: string, search?: string): Observable<RrtMidlaySelectionResponse> {
    let params = new HttpParams();
    if (vceMac) {
      params = params.set('vce_mac', this.macPath(vceMac));
    }
    if (vhubMac) {
      params = params.set('vhub_mac', this.macPath(vhubMac));
    }
    if (search) {
      params = params.set('search', String(search).trim());
    }
    return this.http.get<RrtMidlaySelectionResponse>(
      environment.endpoints.ap + '/rrt-midlay/selection-options',
      { params }
    );
  }

  addMidlayVhubPeer(vhubMac: string, data: any): Observable<any> {
    return this.http.post<any>(environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/peers', data)
  }

  previewMidlayVhubPeer(vhubMac: string, data: any): Observable<any> {
    return this.http.post<any>(environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/peers/preview', data)
  }

  generateMidlayVhubPeerProvisioning(vhubMac: string, peerMac: string, data: any): Observable<any> {
    return this.http.post<any>(
      environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/peers/' + this.macPath(peerMac) + '/provisioning',
      data
    )
  }

  validateMidlayVhubPeerVce(vhubMac: string, peerMac: string, vceMac: string, data: any): Observable<any> {
    return this.http.post<any>(
      environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/peers/' + this.macPath(peerMac) + '/vce/' + this.macPath(vceMac) + '/validate',
      data
    )
  }

  applyMidlayVhubPeerVce(vhubMac: string, peerMac: string, vceMac: string, data: any): Observable<any> {
    return this.http.post<any>(
      environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/peers/' + this.macPath(peerMac) + '/vce/' + this.macPath(vceMac) + '/apply',
      data
    )
  }

  getMidlayVhubState(vhubMac: string): Observable<any> {
    return this.http.get<any>(environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/state')
  }

  getAssociatedMidlayVhubPeers(vceMac: string): Observable<any> {
    return this.http.get<any>(environment.endpoints.ap + '/rrt-midlay/vce/' + this.macPath(vceMac) + '/associated-vhub-peers');
  }

  getMidlayVceState(vceMac: string, team = 'team2'): Observable<any> {
    return this.http.get<any>(
      environment.endpoints.ap + '/rrt-midlay/vce/' + this.macPath(vceMac) + '/state',
      { params: { team } }
    )
  }

  addMidlayVhubPeerAutomation(vhubMac: string, data: any): Observable<any> {
    return this.http.post<any>(environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/peers/add/automation', data)
  }

  runMidlayVhubPeerAutomationStep(vhubMac: string, data: any): Observable<any> {
    return this.http.post<any>(environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/peers/add/automation', data)
  }

  addMidlayVhubDpc(vhubMac: string, data: any): Observable<any> {
    return this.http.post<any>(environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/dpcs/add', data)
  }

  editMidlayVhubDpc(vhubMac: string, dpcName: string, data: any): Observable<any> {
    return this.http.post<any>(
      environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/dpcs/' + encodeURIComponent(dpcName) + '/edit',
      data
    )
  }

  deleteMidlayVhubDpc(vhubMac: string, dpcName: string): Observable<any> {
    return this.http.post<any>(
      environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/dpcs/' + encodeURIComponent(dpcName) + '/delete',
      {}
    )
  }

  editMidlayVhubPeer(vhubMac: string, peerMac: string, data: any): Observable<any> {
    return this.http.post<any>(
      environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/peers/' + this.macPath(peerMac) + '/edit',
      data
    )
  }

  deleteMidlayVhubPeer(vhubMac: string, peerMac: string): Observable<any> {
    return this.http.delete<any>(
      environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/peers/' + this.macPath(peerMac),
      {}
    )
  }

  getMidlayVhubPeerPsk(vhubMac: string, peerMac: string): Observable<any> {
    return this.http.get<any>(
      environment.endpoints.ap + '/rrt-midlay/vhub/' + this.macPath(vhubMac) + '/peers/' + this.macPath(peerMac) + '/psk'
    )
  }
}
